import { type AnyBlock, normalizeString } from '@/components/Form/helper/cdpPrefillHelper';
import { richtextToString } from '@/helper/RichText';
import type { CancellationRetentionTeaserTracking } from '@/helper/tracking';
import { isExternalUrl, linkToString } from '@/helper/urls';
import { hasPersonalizedName } from '@/helper/userPersonalization';
import type {
  FormPageStoryblok,
  FormRadioGroupStoryblok,
  FormSelectStoryblok,
  FormStoryblok,
  GlobalProjectSettingsStoryblok,
  OptionItemStoryblok,
} from '@/types/component-types-sb';

export const PRODUCT_FIELD_ID = 'product';
export const RETENTION_DESKTOP_MIN_WIDTH = 1024;
export const RETENTION_CTA_PATH = '/ueber-uns/aktionen/ich-bleibe-da/';
export const RETENTION_VKN = '431';
export const RETENTION_CAMPAIGN_ID = 'dy_nba_notification_treu';
export const RETENTION_SESSION_DISMISS_KEY = 'cancellation-retention-notification-dismissed';
export const RETENTION_DESKTOP_CTA_LABEL = 'Angebot entdecken';
export const RETENTION_MOBILE_CTA_LABEL = 'Treue-Angebot ansehen';
export const RETENTION_TITLE = 'Kündigung? Wir haben eine bessere Idee';

const RETENTION_PRODUCTS = new Set(['strom', 'gas', 'erdgas']);

type ProductOption = {
  value: string;
  label: string;
};

function isSectionType(block: AnyBlock): boolean {
  return !!block && (block.component === 'FormSection' || block.component === 'FormFieldset');
}

function isProductField(block: AnyBlock): block is FormSelectStoryblok | FormRadioGroupStoryblok {
  return (
    !!block &&
    (block.component === 'FormSelect' || block.component === 'FormRadioGroup') &&
    block.id === PRODUCT_FIELD_ID
  );
}

function mapProductOption(option: OptionItemStoryblok): ProductOption {
  return {
    value: String(option.value),
    label: typeof option.label === 'string' ? option.label : richtextToString(option.label),
  };
}

function collectProductOptions(arr?: AnyBlock[]): ProductOption[] {
  const options: ProductOption[] = [];

  if (!Array.isArray(arr)) {
    return options;
  }

  arr.forEach((block) => {
    if (!block) {
      return;
    }

    if (isSectionType(block)) {
      options.push(...collectProductOptions(block.fields as AnyBlock[] | undefined));
      return;
    }

    if (!isProductField(block)) {
      return;
    }

    block.options?.forEach((option) => {
      if (option && (option as OptionItemStoryblok).component === 'OptionItem') {
        options.push(mapProductOption(option as OptionItemStoryblok));
      }
    });
  });

  return options;
}

export function getProductFieldOptions(blok: FormStoryblok): ProductOption[] {
  const pages = blok.content as FormPageStoryblok[] | undefined;
  const options: ProductOption[] = [];

  pages?.forEach((page) => {
    options.push(...collectProductOptions(page?.content as AnyBlock[] | undefined));
  });

  return options;
}

export function isCancellationForm(blok: FormStoryblok): boolean {
  return blok.action?.some((action) => action.component === 'ContractTerminationAction') ?? false;
}

function isRetentionProductToken(token: string): boolean {
  return RETENTION_PRODUCTS.has(token);
}

export function isRetentionProductSelected(productValue: unknown, blok?: FormStoryblok): boolean {
  if (productValue == null || productValue === '') {
    return false;
  }

  const normalizedValue = normalizeString(productValue);
  if (isRetentionProductToken(normalizedValue)) {
    return true;
  }

  if (!blok) {
    return false;
  }

  const matchedOption = getProductFieldOptions(blok).find(
    (option) => normalizeString(option.value) === normalizedValue || normalizeString(option.label) === normalizedValue
  );

  if (!matchedOption) {
    return false;
  }

  return (
    isRetentionProductToken(normalizeString(matchedOption.value)) ||
    isRetentionProductToken(normalizeString(matchedOption.label))
  );
}

export function resolveRetentionSwdVkn(settings?: GlobalProjectSettingsStoryblok | null): string {
  return settings?.cancellation_retention_swd_vkn?.trim() || RETENTION_VKN;
}

export function resolveRetentionCampaign(settings?: GlobalProjectSettingsStoryblok | null): string {
  return settings?.cancellation_retention_swd_campaign?.trim() || RETENTION_CAMPAIGN_ID;
}

export function resolveRetentionCtaOrigin(settings?: GlobalProjectSettingsStoryblok | null): string {
  if (typeof window !== 'undefined') {
    const { hostname, protocol, port } = window.location;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';

    if (isLocalhost) {
      return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    }

    return process.env.NEXT_PUBLIC_DOMAIN?.trim().replace(/\/$/, '') ?? '';
  }

  const settingsBaseUrl = settings?.base_url?.replace(/\/$/, '');
  if (settingsBaseUrl) {
    return settingsBaseUrl;
  }

  return process.env.NEXT_PUBLIC_DOMAIN?.trim().replace(/\/$/, '') ?? '';
}

export function buildRetentionCtaUrl(
  settings?: GlobalProjectSettingsStoryblok | null,
  origin = resolveRetentionCtaOrigin(settings)
): string {
  const linkPath = linkToString(settings?.cancellation_retention_cta_link) || RETENTION_CTA_PATH;
  const url = isExternalUrl(linkPath) ? new URL(linkPath) : new URL(linkPath, origin);

  if (!url.searchParams.has('swd_vkn')) {
    url.searchParams.set('swd_vkn', resolveRetentionSwdVkn(settings));
  }

  if (!url.searchParams.has('swd_campaign')) {
    url.searchParams.set('swd_campaign', resolveRetentionCampaign(settings));
  }

  return url.toString();
}

export type RetentionCtaNavigationTarget = { type: 'internal'; href: string } | { type: 'external'; href: string };

export function getRetentionCtaNavigationTarget(
  settings?: GlobalProjectSettingsStoryblok | null,
  origin = resolveRetentionCtaOrigin(settings)
): RetentionCtaNavigationTarget {
  const url = buildRetentionCtaUrl(settings, origin);

  if (isExternalUrl(url)) {
    return { type: 'external', href: url };
  }

  const { pathname, search } = new URL(url);
  return { type: 'internal', href: `${pathname}${search}` };
}

export function getRetentionTrackingPayload(
  settings: GlobalProjectSettingsStoryblok | null | undefined,
  linkText: string
): CancellationRetentionTeaserTracking {
  return {
    location: 'nba-notification',
    type: 'notification',
    name: 'kündigunsprävention',
    category: 'pk, kündigungsstrecke',
    title: RETENTION_TITLE,
    segment: 'b2c',
    link_text: linkText,
    swd_campaign: resolveRetentionCampaign(settings),
  };
}

export function getRetentionNotificationMessage(loggedIn: boolean, salutation?: string, lastname?: string): string {
  if (loggedIn && hasPersonalizedName(salutation, lastname)) {
    const displaySalutation = salutation?.trim() || '';
    const displayLastname = lastname?.trim() || '';
    return `${displaySalutation} ${displayLastname}, bevor Sie gehen: Schauen Sie sich unser Treue-Angebot an – vielleicht lohnt sich ein Bleiben.`;
  }

  return 'Bevor Sie gehen: Schauen Sie sich unser Treue-Angebot an – vielleicht lohnt sich ein Bleiben.';
}

export function isRetentionNotificationDismissed(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(RETENTION_SESSION_DISMISS_KEY) === 'true';
  } catch {
    return false;
  }
}

export function dismissRetentionNotification(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(RETENTION_SESSION_DISMISS_KEY, 'true');
  } catch {
    // sessionStorage may be unavailable in private browsing
  }
}
